/**
 * Copyright 2024 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { useEffect } from 'react';
import { useLegacyRouter } from '@/hooks/useLegacyRouter';
import { useRouter } from 'next/navigation';
import { appendSearchTextToUrl, parseUrl, stringifyUrl } from '@/utils/urlHelpers';
import { isEmptyObject } from '@/utils/common';

type UseHandleRedirectProps = {
  redirectPath?: string;
};

export const useHandleRedirect = ({ redirectPath }: UseHandleRedirectProps) => {
  const legacyRouter = useLegacyRouter();
  const router = useRouter();

  useEffect(() => {
    if (!redirectPath) {
      return;
    }

    // Next.js types query params as string | string[]; normalize to a single string
    const searchText = Array.isArray(legacyRouter.query.searchText)
      ? legacyRouter.query.searchText[0]
      : legacyRouter.query.searchText;
    const filterByKeyWord = Array.isArray(legacyRouter.query.filterByKeyWord)
      ? legacyRouter.query.filterByKeyWord[0]
      : legacyRouter.query.filterByKeyWord;
    const redirectUrl = Array.isArray(legacyRouter.query.redirectUrl)
      ? legacyRouter.query.redirectUrl[0]
      : legacyRouter.query.redirectUrl;

    const updatedRedirect = appendSearchTextToUrl(redirectPath, searchText, filterByKeyWord);

    const parsedPath = parseUrl(updatedRedirect);
    let searchParams = {};
    let sanitizedPath = updatedRedirect;

    if (!isEmptyObject(parsedPath?.query)) {
      searchParams = parsedPath.query;
      sanitizedPath = parsedPath.url;
    }

    const hash = legacyRouter.asPath.includes('#') ? legacyRouter.asPath.split('#')[1] : '';
    const queryObj = {
      ...searchParams,
      ...(redirectUrl && { redirectUrl: 'true' }),
    };

    const url = stringifyUrl({
      url: sanitizedPath,
      query: queryObj,
    });
    router.replace(hash ? `${url}#${hash}` : url);
  }, [redirectPath, legacyRouter.asPath, legacyRouter.query, router]);
};
